import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { 
  getStatistics, 
  getTimeSeriesData, 
  exportToCSV
} from '../utils/tracking';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to last 30 days
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Statistics state
  const [stats, setStats] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  
  // Default password - CHANGE THIS to your secure password
  const ADMIN_PASSWORD = 'admin2025'; // TODO: Change this password
  
  const loadData = useCallback(() => {
    try {
      const statistics = getStatistics(startDate, endDate);
      // Ensure statistics object has all required properties
      const safeStats = {
        totalEvents: statistics?.totalEvents || 0,
        linkClicks: statistics?.linkClicks || {},
        formStarts: statistics?.formStarts || {},
        formSubmits: statistics?.formSubmits || {},
        formAbandonments: statistics?.formAbandonments || {},
        blogViews: statistics?.blogViews || {},
        buttonClicks: statistics?.buttonClicks || {},
        pageViews: statistics?.pageViews || {},
        formDropOffRate: statistics?.formDropOffRate || '0',
        totalFormStarts: statistics?.totalFormStarts || 0,
        totalFormSubmits: statistics?.totalFormSubmits || 0,
        totalFormAbandonments: statistics?.totalFormAbandonments || 0,
        totalBlogViews: statistics?.totalBlogViews || 0,
        totalLinkClicks: statistics?.totalLinkClicks || 0
      };
      setStats(safeStats);
      
      // Load time series data for various event types
      try {
        const allEvents = getTimeSeriesData(startDate, endDate);
        // Validate time series data structure - ensure it's always an array with valid objects
        let safeTimeSeries = [];
        if (Array.isArray(allEvents) && allEvents.length > 0) {
          safeTimeSeries = allEvents
            .filter(item => item && typeof item === 'object')
            .map(item => {
              const date = item?.date || '';
              const count = typeof item?.count === 'number' ? item.count : 0;
              const formattedDate = item?.formattedDate || date || '';
              return {
                date: date,
                count: count,
                formattedDate: formattedDate
              };
            })
            .filter(item => item.date && item.formattedDate); // Remove invalid entries
        }
        
        // Don't set empty array - let the component handle empty state
        setTimeSeriesData(safeTimeSeries);
      } catch (tsError) {
        console.error('Error loading time series data:', tsError);
        // Set empty array - component will handle empty state
        setTimeSeriesData([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Set safe defaults
      setStats({
        totalEvents: 0,
        linkClicks: {},
        formStarts: {},
        formSubmits: {},
        formAbandonments: {},
        blogViews: {},
        buttonClicks: {},
        pageViews: {},
        formDropOffRate: '0',
        totalFormStarts: 0,
        totalFormSubmits: 0,
        totalFormAbandonments: 0,
        totalBlogViews: 0,
        totalLinkClicks: 0
      });
      setTimeSeriesData([]);
    }
  }, [startDate, endDate]);
  
  useEffect(() => {
    // Check if already authenticated
    const authStatus = sessionStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);
  
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_authenticated', 'true');
      setError('');
      loadData();
    } else {
      setError('Incorrect password. Access denied.');
      setPassword('');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    setPassword('');
  };
  
  const handleExportCSV = () => {
    const csvData = exportToCSV(startDate, endDate);
    if (!csvData) {
      alert('No data available for the selected date range.');
      return;
    }
    
    // Create blob and download
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tracking-data-${startDate}-to-${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const COLORS = ['#1F3A93', '#4A90E2', '#7BB3F0', '#A6D4FF', '#D4E8FF'];
  
  // Prepare chart data with safe guards - wrapped in try-catch
  let linkClicksChartData = [];
  let blogViewsChartData = [];
  let formStatsData = [];
  
  try {
    if (stats && stats.linkClicks && typeof stats.linkClicks === 'object') {
      linkClicksChartData = Object.entries(stats.linkClicks)
        .filter(([name, value]) => name && typeof value === 'number')
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .slice(0, 10)
        .map(([name, value]) => ({ 
          name: name && name.length > 30 ? name.substring(0, 30) + '...' : name || 'Unknown', 
          value: value || 0 
        }));
    }
  } catch (e) {
    console.error('Error preparing link clicks chart data:', e);
    linkClicksChartData = [];
  }

  try {
    if (stats && stats.blogViews && typeof stats.blogViews === 'object') {
      blogViewsChartData = Object.entries(stats.blogViews)
        .filter(([name, value]) => name && typeof value === 'number')
        .map(([name, value]) => ({ name: name || 'Unknown', value: value || 0 }));
    }
  } catch (e) {
    console.error('Error preparing blog views chart data:', e);
    blogViewsChartData = [];
  }

  try {
    if (stats) {
      formStatsData = [
        { name: 'Form Starts', value: stats.totalFormStarts || 0 },
        { name: 'Form Submits', value: stats.totalFormSubmits || 0 },
        { name: 'Form Abandonments', value: stats.totalFormAbandonments || 0 }
      ];
    }
  } catch (e) {
    console.error('Error preparing form stats data:', e);
    formStatsData = [];
  }
  
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h2 style={{ marginBottom: '20px', color: '#1F3A93' }}>Admin Access</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>Please enter the password to access the dashboard.</p>
            <form onSubmit={handleLogin}>
              <label htmlFor="admin-password" style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Password
              </label>
              <input
                type="password"
                id="admin-password"
                name="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
                autoFocus
              />
              {error && <p style={{ color: 'red', marginBottom: '15px', fontSize: '14px' }}>{error}</p>}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1F3A93',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Login
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Internal Use Only</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="description" content="Internal admin dashboard" />
      </Helmet>
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{ margin: 0, color: '#1F3A93' }}>Website Analytics Dashboard</h1>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Logout
            </button>
          </div>
          
          {/* Date Range Selector */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <label htmlFor="start-date" style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Start Date:</label>
                <input
                  type="date"
                  id="start-date"
                  name="start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label htmlFor="end-date" style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>End Date:</label>
                <input
                  type="date"
                  id="end-date"
                  name="end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={handleExportCSV}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Export to CSV
                </button>
              </div>
            </div>
          </div>
          
          {/* Statistics Cards */}
          {stats ? (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Total Events</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#1F3A93' }}>
                    {(stats.totalEvents || 0).toLocaleString()}
                  </p>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Link Clicks</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#4A90E2' }}>
                    {(stats.totalLinkClicks || 0).toLocaleString()}
                  </p>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Blog Views</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#7BB3F0' }}>
                    {(stats.totalBlogViews || 0).toLocaleString()}
                  </p>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Form Starts</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#A6D4FF' }}>
                    {(stats.totalFormStarts || 0).toLocaleString()}
                  </p>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Form Submits</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
                    {(stats.totalFormSubmits || 0).toLocaleString()}
                  </p>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>Form Drop-off Rate</h3>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#dc3545' }}>
                    {stats.formDropOffRate || '0'}%
                  </p>
                </div>
              </div>
              
              {/* Charts Section */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                gap: '20px',
                marginBottom: '20px'
              }}>
                {/* Time Series Chart */}
                {(() => {
                  try {
                    // Validate data structure before rendering
                    const validData = Array.isArray(timeSeriesData) && timeSeriesData.length > 0
                      ? timeSeriesData.filter(item => 
                          item && 
                          typeof item === 'object' && 
                          typeof item.count === 'number' && 
                          item.formattedDate
                        )
                      : [];
                    
                    // Only render chart if we have valid, non-empty data
                    if (validData.length > 0 && validData.every(item => 
                      item && 
                      typeof item === 'object' && 
                      typeof item.count === 'number' && 
                      item.count >= 0 &&
                      typeof item.formattedDate === 'string' && 
                      item.formattedDate.length > 0
                    )) {
                      // Double-check data structure and ensure all values are valid
                      const chartData = validData
                        .map(item => {
                          const count = Number(item.count);
                          const formattedDate = String(item.formattedDate || '');
                          if (isNaN(count) || !formattedDate) return null;
                          return {
                            formattedDate: formattedDate,
                            count: count
                          };
                        })
                        .filter(item => item !== null && item.count !== undefined && item.formattedDate);
                      
                      // Only render chart if we still have valid data after processing
                      if (chartData.length > 0) {
                        return (
                          <div style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Daily Activity Trend</h3>
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                  dataKey="formattedDate"
                                  type="category"
                                  allowDuplicatedCategory={false}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Line 
                                  type="monotone" 
                                  dataKey="count" 
                                  stroke="#1F3A93" 
                                  strokeWidth={2} 
                                  name="Events"
                                  dot={{ fill: '#1F3A93', r: 3 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      }
                    }
                    
                    // No valid data - show empty state
                    return (
                      <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Daily Activity Trend</h3>
                        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                          No data available for the selected date range
                        </div>
                      </div>
                    );
                  } catch (error) {
                    console.error('Error rendering time series chart:', error);
                    return (
                      <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Daily Activity Trend</h3>
                        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc3545' }}>
                          Error loading chart
                        </div>
                      </div>
                    );
                  }
                })()}
                
                {/* Top Link Clicks */}
                {(() => {
                  try {
                    if (linkClicksChartData && Array.isArray(linkClicksChartData) && linkClicksChartData.length > 0) {
                      return (
                        <div style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Top 10 Link Clicks</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={linkClicksChartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#4A90E2" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    }
                    return null;
                  } catch (error) {
                    console.error('Error rendering link clicks chart:', error);
                    return null;
                  }
                })()}
                
                {/* Blog Views */}
                {(() => {
                  try {
                    if (blogViewsChartData && Array.isArray(blogViewsChartData) && blogViewsChartData.length > 0) {
                      return (
                        <div style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Blog Views by Article</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={blogViewsChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {blogViewsChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    }
                    return null;
                  } catch (error) {
                    console.error('Error rendering blog views chart:', error);
                    return null;
                  }
                })()}
                
                {/* Form Statistics */}
                {(() => {
                  try {
                    if (formStatsData && Array.isArray(formStatsData) && formStatsData.some(item => item && item.value > 0)) {
                      return (
                        <div style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Form Statistics</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={formStatsData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#7BB3F0" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    }
                    return null;
                  } catch (error) {
                    console.error('Error rendering form statistics chart:', error);
                    return null;
                  }
                })()}
              </div>
              
              {/* Detailed Tables */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '20px'
              }}>
                {/* Blog Views Detail */}
                {stats.blogViews && typeof stats.blogViews === 'object' && Object.keys(stats.blogViews).length > 0 && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Blog Views Detail</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Blog</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Views</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(stats.blogViews)
                          .sort((a, b) => b[1] - a[1])
                          .map(([blog, views]) => (
                            <tr key={blog} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '10px' }}>{blog}</td>
                              <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{views}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Form Details */}
                {stats.formStarts && typeof stats.formStarts === 'object' && Object.keys(stats.formStarts).length > 0 && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Form Analytics</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Form</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Starts</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Submits</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Abandoned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(stats.formStarts).map((formName) => {
                          const starts = stats.formStarts[formName] || 0;
                          const submits = stats.formSubmits[formName] || 0;
                          const abandoned = stats.formAbandonments[formName] || 0;
                          return (
                            <tr key={formName} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '10px' }}>{formName}</td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>{starts}</td>
                              <td style={{ padding: '10px', textAlign: 'right', color: '#28a745' }}>{submits}</td>
                              <td style={{ padding: '10px', textAlign: 'right', color: '#dc3545' }}>{abandoned}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#666', marginBottom: '10px' }}>No Data Available</h3>
              <p style={{ color: '#999' }}>Start tracking user interactions to see analytics data here.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;

